import React, { useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';

import { useCashflowForecastTableContext } from './CashflowForecastReportTableContext';
import { COMMITMENT_VIEWS } from './commitmentViews';

import { formatFigure, formatToFixedMoneyWithLocale } from 'Helpers/formatService';

import Tooltip from 'CommonComponents/Tooltip';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';

/**
 * Render a numeric cell value.
 *
 * - committed rows with no data in a month: the zero dash
 * - uncommitted rows and tax rows with no data: blank
 * - negative values: tw-text-red-600
 */
function NumericCell({ value, className = '' }) {
    if (value === null || value === undefined) {
        return <td className={classNames('tw-text-right tw-whitespace-nowrap', className)}></td>;
    }

    const numVal = parseFloat(value) || 0;
    const isNegative = numVal < 0;
    const formatted = formatFigure(numVal);

    return (
        <td
            className={classNames('tw-text-right tw-whitespace-nowrap', className, {
                'tw-text-red-600': isNegative,
            })}
        >
            {formatted}
        </td>
    );
}

/**
 * Closing Balance cell with inline editing.
 * Click to edit, blur/Enter to save, Escape to cancel.
 * Amber background when an override is active.
 */
function ClosingBalanceCell({ monthKey, displayValue, isOverridden, isLocked }) {
    const { saveOverride, pendingOverrides } = useCashflowForecastTableContext();
    const [editing, setEditing] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const inputRef = useRef(null);
    const pendingState = pendingOverrides[monthKey];

    const handleClick = () => {
        if (isLocked || editing) return;
        // Pre-fill input with current display value (raw number, no formatting)
        const raw = displayValue !== null && displayValue !== undefined ? String(parseFloat(displayValue) || 0) : '';
        setInputValue(raw);
        setEditing(true);
    };

    useEffect(() => {
        if (editing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [editing]);

    const handleSave = () => {
        setEditing(false);
        const trimmed = inputValue.trim();
        // Empty input = clear override
        if (trimmed === '') {
            saveOverride(monthKey, null);
            return;
        }
        const num = parseFloat(trimmed);
        if (isNaN(num)) return;
        // Don't create an override when the value is unchanged from the calculated balance
        if (!isOverridden) {
            const calculatedVal = displayValue !== null && displayValue !== undefined ? parseFloat(displayValue) : null;
            if (calculatedVal !== null && num === calculatedVal) return;
        }
        saveOverride(monthKey, num);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSave();
        } else if (e.key === 'Escape') {
            setEditing(false);
        }
    };

    const numVal = displayValue !== null && displayValue !== undefined ? parseFloat(displayValue) : null;
    const isNegative = numVal !== null && numVal < 0;
    // Display as a whole number for consistency with every other row in the report.
    // The raw decimal value is still used to pre-fill the inline editor (see handleClick).
    const formatted = numVal !== null ? formatToFixedMoneyWithLocale(numVal) : '—';

    const cellClasses = classNames('tw-text-right tw-whitespace-nowrap tw-relative', {
        '!tw-bg-amber-50': isOverridden && !editing,
        'tw-cursor-pointer': !isLocked,
        '!tw-text-red-600': isNegative && !editing,
        '!tw-text-black': true,
    });

    if (editing) {
        return (
            <td className={classNames('tw-text-right tw-whitespace-nowrap tw-p-0 !tw-text-black', { '!tw-bg-amber-50': isOverridden })}>
                <input
                    ref={inputRef}
                    type="number"
                    step="0.01"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onBlur={handleSave}
                    onKeyDown={handleKeyDown}
                    className="tw-w-full tw-text-right tw-bg-white tw-border tw-border-blue tw-rounded-none tw-px-1 tw-py-0 tw-text-sm tw-text-charcoal tw-outline-none"
                    style={{ minWidth: '80px' }}
                />
            </td>
        );
    }

    return (
        <td className={cellClasses} onClick={handleClick} title={isLocked ? undefined : $t('Click to override closing balance')}>
            <span className="tw-flex tw-items-center tw-justify-end tw-gap-1">
                {isOverridden && (
                    <span className="tw-inline-block tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-amber-400 tw-shrink-0" title={$t('Override active')} />
                )}
                <span>{formatted}</span>
                {pendingState === 'saving' && <span className="tw-text-xs tw-text-charcoal-50 tw-ml-0.5">…</span>}
            </span>
        </td>
    );
}

function SectionHeader({ label, months, section, collapsed, onToggle }) {
    return (
        <tr className="tw-text-sm tw-cursor-pointer tw-select-none tw-uppercase !tw-bg-blue tw-text-white" onClick={() => onToggle(section)}>
            <td className="tw-sticky tw-left-0 tw-uppercase !tw-bg-blue tw-text-white tw-z-10 tw-font-semibold tw-text-white tw-whitespace-nowrap">
                <span className="tw-flex tw-items-center tw-gap-1.5">
                    {label}
                    <span
                        className={classNames('tw-transition-transform tw-duration-150', {
                            'tw-rotate-180': !collapsed,
                        })}
                        style={{ display: 'inline-block' }}
                    >
                        <ChevronDownIcon className="tw-w-1.5 tw-h-1.5" />
                    </span>
                </span>
            </td>
            <td colSpan={months.length}></td>
        </tr>
    );
}

function TotalRow({ label, months, valueMap, className = '', emptyDash = false }) {
    return (
        <tr className={classNames('tw-font-semibold tw-text-sm !tw-bg-[#D8D8D8] !tw-text-black', className)}>
            <td className="tw-sticky tw-left-0 tw-z-10 tw-whitespace-nowrap !tw-bg-[#D8D8D8] !tw-text-black">{label}</td>
            {months.map((m) => (
                <NumericCell key={m} value={valueMap[m] ?? (emptyDash ? null : 0)} className="!tw-text-black" />
            ))}
        </tr>
    );
}

const PAYROLL_TOOLTIP = $t(
    'This amount includes salary and employer on costs. The full amount is treated as a cash outflow in the month incurred. If your payroll tax remittance schedule differs (e.g., quarterly), you can model this using a tax line in Cashflow Settings.',
);

const BEST_CASE_TOOLTIP = $t('Best case assumes all uncommitted expenditure does not occur, resulting in a higher closing balance.');

const WORST_CASE_TOOLTIP = $t('Worst case assumes all uncommitted income is not received, resulting in a lower closing balance.');

const BALANCE_VIEW_NOTE = $t('Tax payments, balances and net cashflow always reflect total figures, whichever view is selected.');

/**
 * Small circled-info icon that reveals explanatory text on hover.
 */
function InfoIcon({ content }) {
    return (
        <Tooltip tooltipType="text" tooltipContent={content} placement="top">
            <span className="tw-ms-1.5 tw-inline-flex tw-items-center tw-text-charcoal-50 tw-cursor-help">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="tw-w-3.5 tw-h-3.5">
                    <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
                        clipRule="evenodd"
                    />
                </svg>
            </span>
        </Tooltip>
    );
}

function WorkforceInfoIcon() {
    return <InfoIcon content={PAYROLL_TOOLTIP} />;
}

function TaxLineRow({ line, months, vatGstEnabled }) {
    const { vatGstExpanded, toggleVatGst } = useCashflowForecastTableContext();
    const isVatGst = line.type === 'vat_gst';
    const isGreyed = isVatGst && !vatGstEnabled;
    const hasChildren = isVatGst && Array.isArray(line.children) && line.children.length > 0;

    return (
        <>
            <tr
                className={classNames('tw-text-sm !tw-bg-white', {
                    'tw-text-charcoal-50': isGreyed,
                    'tw-text-charcoal': !isGreyed,
                    'tw-cursor-pointer tw-select-none': hasChildren,
                })}
                onClick={hasChildren ? toggleVatGst : undefined}
            >
                <td className="tw-sticky tw-left-0 tw-bg-white tw-z-10 tw-whitespace-nowrap">
                    <span className="tw-flex tw-items-center tw-gap-1.5">
                        {line.label}
                        {isVatGst && (
                            <span className="tw-ms-1.5 tw-inline-flex tw-items-center tw-rounded tw-bg-charcoal-10 tw-px-1 tw-text-xs tw-font-medium tw-text-charcoal-75">
                                {$t('net')}
                            </span>
                        )}
                        {hasChildren && (
                            <span
                                className={classNames('tw-transition-transform tw-duration-150', { 'tw-rotate-180': vatGstExpanded })}
                                style={{ display: 'inline-block' }}
                            >
                                <ChevronDownIcon className="tw-w-3 tw-h-3" />
                            </span>
                        )}
                    </span>
                </td>
                {months.map((m) => (
                    <NumericCell key={m} value={line.months[m] ?? null} className={isGreyed ? 'tw-text-charcoal-50' : ''} />
                ))}
            </tr>

            {hasChildren &&
                vatGstExpanded &&
                line.children.map((child) => (
                    <tr key={child.id} className="tw-text-sm tw-text-charcoal !tw-bg-white">
                        <td className="tw-sticky tw-left-0 tw-bg-white tw-z-10 tw-whitespace-nowrap tw-ps-8">{child.label}</td>
                        {months.map((m) => (
                            <NumericCell key={m} value={child.months[m] ?? null} />
                        ))}
                    </tr>
                ))}
        </>
    );
}

function SummaryRow({ label, months, valueMap, highlight = false, className = '', tooltip = null }) {
    return (
        <tr
            className={classNames('tw-text-sm tw-font-semibold', className, {
                '!tw-bg-[#D8D8D8] !tw-text-black': highlight,
                '!tw-bg-charcoal !tw-text-white': !highlight,
            })}
        >
            <td
                className={classNames('tw-sticky tw-left-0 tw-z-10 tw-whitespace-nowrap', {
                    '!tw-bg-[#D8D8D8] !tw-text-black': highlight,
                    '!tw-bg-charcoal !tw-text-white': !highlight,
                })}
            >
                {tooltip ? (
                    <span className="tw-inline-flex tw-items-center">
                        {label}
                        <InfoIcon content={tooltip} />
                    </span>
                ) : (
                    label
                )}
            </td>
            {months.map((m) => {
                const val = valueMap ? valueMap[m] : null;
                return <NumericCell key={m} value={val} className={highlight ? 'tw-text-black' : '!tw-text-white'} />;
            })}
        </tr>
    );
}

/**
 * Pick the month map a node should render under the selected view.
 *
 * No fallback to `months` for Committed / Uncommitted: the table always renders a fresh
 * `fetchData()` response (mount, start-month change), and the controller emits all three
 * maps on every path, so a node missing its own map has none — rendering it as empty is
 * the honest failure mode, not silently doubling the figure under a Committed/Uncommitted heading.
 */
function valuesForView(node, view) {
    if (!node) return {};
    if (view === COMMITMENT_VIEWS.COMMITTED) return node.committed || {};
    if (view === COMMITMENT_VIEWS.UNCOMMITTED) return node.uncommitted || {};
    return node.months || {};
}

/**
 * Resolve a whole income/expenditure tree onto the selected view once, so a cell render
 * is a lookup rather than a per-cell map choice.
 */
function resolveTreeForView(nodes, view) {
    return (nodes || []).map((node) => ({
        ...node,
        values: valuesForView(node, view),
        children: resolveTreeForView(node.children, view),
    }));
}

/**
 * Row background by depth. A department keeps its banding even with no children, which
 * happens when its only value is small enough that the remainder row is suppressed and
 * no cost centre resolved — it is still a department row and must not read as a leaf.
 */
function rowBackground(level, hasChildren) {
    if (level === 0) return '!tw-bg-charcoal-15';
    return hasChildren ? '!tw-bg-charcoal-10' : '!tw-bg-white';
}

// Max reachable depth is 2 (expenditure: department -> branch -> cost centre).
const INDENT_CLASSES = ['', 'tw-ps-6', 'tw-ps-12'];

/**
 * One node of an income or expenditure tree, with its descendants.
 *
 * Both sections share this row: income runs Department → cost centre / tier / sport,
 * expenditure runs Department → Workforce / Non-Workforce → cost centre. Rows with
 * children are collapsed by default and toggle independently; the global
 * deptExpandTrigger from context drives Expand all / Collapse all, and newly mounted
 * descendants inherit it so Expand all reaches the deepest level.
 */
function HierarchyRow({ node, months, level = 0 }) {
    const { deptExpandTrigger } = useCashflowForecastTableContext();

    // Seeding from the trigger is deliberate: descendants mount only once their parent is
    // already expanded, so the effect below can never reach them and Expand all would stop
    // one level short. The cost is that re-expanding a manually collapsed branch shows its
    // children expanded again.
    const [expanded, setExpanded] = useState(deptExpandTrigger.version > 0 ? deptExpandTrigger.expand : false);

    useEffect(() => {
        if (deptExpandTrigger.version > 0) {
            setExpanded(deptExpandTrigger.expand);
        }
    }, [deptExpandTrigger.version]);

    const children = node.children || [];
    const hasChildren = children.length > 0;
    const background = rowBackground(level, hasChildren);
    const indent = INDENT_CLASSES[Math.min(level, INDENT_CLASSES.length - 1)];

    const toggle = () => setExpanded((prev) => !prev);

    return (
        <>
            <tr
                className={classNames('tw-text-sm', background, {
                    'tw-font-medium': level === 0,
                    'tw-text-charcoal': !hasChildren,
                })}
            >
                <td className={classNames('tw-sticky tw-left-0 tw-z-10 tw-whitespace-nowrap', background, indent)}>
                    <span
                        className={classNames('tw-flex tw-items-center tw-gap-1.5', { 'tw-cursor-pointer tw-select-none': hasChildren })}
                        role={hasChildren ? 'button' : undefined}
                        tabIndex={hasChildren ? 0 : undefined}
                        aria-expanded={hasChildren ? expanded : undefined}
                        onClick={hasChildren ? toggle : undefined}
                        onKeyDown={
                            hasChildren
                                ? (event) => {
                                      // Space on a role="button" still scrolls the page
                                      // unless it is claimed here.
                                      if (event.key === 'Enter' || event.key === ' ') {
                                          event.preventDefault();
                                          toggle();
                                      }
                                  }
                                : undefined
                        }
                    >
                        {node.label}
                        {node.state === 'workforce' && <WorkforceInfoIcon />}
                        {hasChildren && (
                            <span
                                className={classNames('tw-transition-transform tw-duration-150', { 'tw-rotate-180': expanded })}
                                style={{ display: 'inline-block' }}
                            >
                                <ChevronDownIcon className="tw-w-3 tw-h-3" />
                            </span>
                        )}
                    </span>
                </td>
                {months.map((m) => (
                    <NumericCell key={m} value={node.values[m] ?? 0} className={background} />
                ))}
            </tr>

            {expanded && children.map((child, i) => <HierarchyRow key={`${child.state}-${child.label}-${i}`} node={child} months={months} level={level + 1} />)}
        </>
    );
}

/**
 * Full-width note pinned above the tax and balance block while a non-total view is
 * selected — everything from here down stays on total figures regardless of the toggle.
 */
function BalanceViewNote({ months }) {
    return (
        <tr className="tw-text-xs !tw-bg-blue-25 tw-text-charcoal-75">
            <td className="tw-sticky tw-left-0 tw-z-10 !tw-bg-blue-25 tw-italic" colSpan={months.length + 1}>
                {BALANCE_VIEW_NOTE}
            </td>
        </tr>
    );
}

/**
 * Pick a section's subtotal map for the selected view. No fallback to `subtotal` for
 * Committed / Uncommitted — see valuesForView.
 */
function subtotalForView(section, view) {
    if (view === COMMITMENT_VIEWS.COMMITTED) return section.subtotalCommitted || {};
    if (view === COMMITMENT_VIEWS.UNCOMMITTED) return section.subtotalUncommitted || {};
    return section.subtotal || {};
}

export default function CashflowForecastReportTable({ data }) {
    const { collapsed, toggleSection, overriddenMonths, view } = useCashflowForecastTableContext();

    const { months, rows } = data;
    const vatGstEnabled = (rows.tax.lines.find((l) => l.type === 'vat_gst') || {}).enabled || false;

    // Opening balance row
    const openingBalance = rows.openingBalance || {};

    // Both sections are hierarchical: the toggle is a client-side selection over one payload,
    // so resolve every node onto the chosen view once rather than per cell per render.
    const incomeLines = useMemo(() => resolveTreeForView(rows.income.lines, view), [rows.income.lines, view]);
    const expenditureLines = useMemo(() => resolveTreeForView(rows.expenditure.lines, view), [rows.expenditure.lines, view]);

    const incomeSubtotal = subtotalForView(rows.income, view);
    const expenditureSubtotal = subtotalForView(rows.expenditure, view);

    return (
        <div className="inbud-table_wrapper">
            <table className="inbud-table">
                <thead>
                    <tr>
                        <th className="!tw-text-left tw-sticky tw-left-0 tw-bg-blue-25 tw-z-10 tw-whitespace-nowrap tw-min-w-[220px]">{$t('Description')}</th>
                        {months.map((m) => (
                            <th key={m} className="!tw-text-right tw-whitespace-nowrap tw-min-w-[110px] tw-bg-blue-25">
                                {m}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {/* Opening Balance */}
                    <SummaryRow label={$t('Opening Balance')} months={months} valueMap={openingBalance} highlight />

                    {/* INCOME section — Department → cost centre / sponsor tier / sport */}
                    <SectionHeader label={$t('INCOME')} months={months} section="income" collapsed={collapsed.income} onToggle={toggleSection} />
                    {!collapsed.income && incomeLines.map((line, i) => <HierarchyRow key={`income-${i}`} node={line} months={months} />)}
                    <TotalRow label={$t('Total income')} months={months} valueMap={incomeSubtotal} />

                    {/* EXPENDITURE section — Department → Workforce/Non-Workforce → cost centre */}
                    <SectionHeader label={$t('EXPENDITURE')} months={months} section="expenditure" collapsed={collapsed.expenditure} onToggle={toggleSection} />
                    {!collapsed.expenditure && expenditureLines.map((line, i) => <HierarchyRow key={`exp-${i}`} node={line} months={months} />)}
                    <TotalRow label={$t('Total expenditure')} months={months} valueMap={expenditureSubtotal} />

                    {/* Tax and balance block — always total figures, whichever view is selected */}
                    {view !== COMMITMENT_VIEWS.TOTAL && <BalanceViewNote months={months} />}

                    {/* TAX PAYMENTS section */}
                    <SectionHeader label={$t('TAX PAYMENTS')} months={months} section="tax" collapsed={collapsed.tax} onToggle={toggleSection} />
                    {!collapsed.tax && rows.tax.lines.map((line, i) => <TaxLineRow key={i} line={line} months={months} vatGstEnabled={vatGstEnabled} />)}
                    <TotalRow label={$t('Total tax payments')} months={months} valueMap={rows.tax.subtotal} emptyDash />

                    {/* Net Cashflow */}
                    <SummaryRow label={$t('Net Cashflow')} months={months} valueMap={rows.netCashflow} highlight />

                    {/* Closing Balance — editable cells */}
                    <tr className="tw-text-sm tw-font-semibold !tw-bg-[#D8D8D8] !tw-text-black">
                        <td className="tw-sticky tw-left-0 tw-z-10 tw-whitespace-nowrap !tw-bg-[#D8D8D8] !tw-text-black">{$t('Closing Balance')}</td>
                        {months.map((m) => {
                            const totals = (rows.monthlyTotals || {})[m] || {};
                            const displayValue = totals.closingBalanceOverride != null ? totals.closingBalanceOverride : totals.closingBalance;
                            const isOverridden = overriddenMonths.has(m);
                            return <ClosingBalanceCell key={m} monthKey={m} displayValue={displayValue} isOverridden={isOverridden} isLocked={false} />;
                        })}
                    </tr>

                    {/* Best Case */}
                    <SummaryRow label={$t('Best case')} months={months} valueMap={rows.bestCase} tooltip={BEST_CASE_TOOLTIP} />

                    {/* Worst Case */}
                    <SummaryRow label={$t('Worst case')} months={months} valueMap={rows.worstCase} tooltip={WORST_CASE_TOOLTIP} />
                </tbody>
            </table>
        </div>
    );
}
